"use client";

import { useState } from "react";

type FaqItem = {
  question: string;
  answer: string;
};

type FaqAccordionProps = {
  items: FaqItem[];
};

// Presentation only. The FAQPage JSON-LD for these same items is emitted from the
// server component that owns the page (see HomeStructuredData): this accordion is
// a client component, so anything it rendered would land in the HTML only after
// hydration — too late for a crawler that never runs the JavaScript.
//
// CSS transitions, not framer-motion: it is on every lander, and framer-motion was the biggest
// thing in their first-load JS. A closed answer stays in the DOM at a 0fr grid row, invisible
// (out of the tab order, the accessibility tree and find-in-page); visibility flips at the end
// of the closing transition, so the fade still plays. Timings match the framer-motion version:
// height and opacity 280 ms ease-out-expo, the "+" turns in 200 ms easeOut. Under reduced motion
// the height and the turn are instant and only the fade plays, as MotionConfig reducedMotion="user" did.
export function FaqAccordion({ items }: FaqAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <>
      <dl className="divide-y divide-rule border-y border-rule">
        {items.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={index}>
              {/* The <dt> must be a direct child of the group for a valid <dl>;
                  the toggle button lives inside it. */}
              <dt>
                <button
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-baseline justify-between gap-4 py-5 text-left"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  type="button"
                >
                  <span className="font-display text-lg tracking-tight text-ink md:text-xl">{item.question}</span>
                  <span
                    aria-hidden="true"
                    className={`font-mono text-base text-ink-faint transition-[rotate] duration-200 ease-[cubic-bezier(0,0,0.58,1)] motion-reduce:transition-none ${isOpen ? "rotate-45" : ""}`}
                  >
                    +
                  </span>
                </button>
              </dt>
              <dd
                className={`grid overflow-hidden font-sans text-base leading-[1.6] text-ink-soft transition-[grid-template-rows,opacity,visibility] duration-[280ms] ease-out-expo motion-reduce:transition-[opacity,visibility] ${
                  isOpen ? "visible grid-rows-[1fr] opacity-100" : "invisible grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="min-h-0">
                  <div className="pb-6 pr-10">{item.answer}</div>
                </div>
              </dd>
            </div>
          );
        })}
      </dl>
    </>
  );
}
